// SCR-STAFF-505 (AP-CLN-04): حاشیه‌نویسی تصویر بالینی به‌صورت لایه جدا (US-CLN-072؛ C-AGENT-1473)، خروجی بازاریابی یا آموزشی فقط با رضایت هدف و
// پوشش چشم/صورت (US-CLN-073؛ AC-CLN-073.1، 073.2؛ C-AGENT-1474، 1475) و ارسال به پرتال مراجعه‌کننده با رضایت پردازش داده (AC-CLN-073.3).
// تصویر خروجی در مرورگر با Canvas به PNG رندر می‌شود (بدون فراداده) و سرور پوشش را دوباره اعمال و هر فراداده‌ای را حذف می‌کند.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { unwrap, type ClnAnnotationKind, type ClnAnnotationView, type ClnImageExportPurpose, type ClnImagePointDto } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type MouseEvent } from 'react';
import { SCR_STAFF_505 } from './beautyCommon';
import { FACE_AREAS } from './bodyRegions';
import { useClinicalImage } from './ClinicalImages';

type Tool = ClnAnnotationKind | 'Mask';
type Mask = { kind: 'Eyes' | 'Face' | 'Custom'; x: number; y: number; width: number; height: number };

const TOOLS: readonly Tool[] = ['Marker', 'Measure', 'Arrow', 'Area', 'Note', 'Mask'];
const NEEDED: Record<ClnAnnotationKind, number> = { Marker: 1, Note: 1, Measure: 2, Arrow: 2, Area: 3 };
const PRESETS: Record<'Eyes' | 'Face', Mask> = {
  Eyes: { kind: 'Eyes', x: 0, y: 0.3, width: 1, height: 0.16 },
  Face: { kind: 'Face', x: 0.15, y: 0.08, width: 0.7, height: 0.8 },
};
const PURPOSES: readonly NonNullable<ClnImageExportPurpose>[] = ['Marketing', 'Education'];

const keys = {
  annotations: (tenantId: string | undefined, imageId: string) => ['clinical', tenantId, 'image-annotations', imageId] as const,
  consents: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'image-consents', patientId] as const,
};

function shape(annotation: { kind: ClnAnnotationKind; points: readonly ClnImagePointDto[]; color?: string | null }, key: string) {
  const color = annotation.color ?? '#ffeb3b';
  const pts = annotation.points.map((point) => ({ x: Number(point.x) * 100, y: Number(point.y) * 100 }));
  const first = pts[0];
  if (!first) {
    return null;
  }
  switch (annotation.kind) {
    case 'Marker':
    case 'Note':
      return <circle key={key} cx={first.x} cy={first.y} r={1.6} fill="none" stroke={color} strokeWidth={0.6} data-testid="cln-annotation-shape" />;
    case 'Measure':
    case 'Arrow': {
      const second = pts[1] ?? first;
      return <line key={key} x1={first.x} y1={first.y} x2={second.x} y2={second.y} stroke={color} strokeWidth={0.6} markerEnd={annotation.kind === 'Arrow' ? 'url(#cln-arrow)' : undefined} data-testid="cln-annotation-shape" />;
    }
    default:
      return <polygon key={key} points={pts.map((point) => `${point.x},${point.y}`).join(' ')} fill="none" stroke={color} strokeWidth={0.6} data-testid="cln-annotation-shape" />;
  }
}

/** رسم حاشیه‌نویسی و پوشش روی Canvas (خروجی). */
function draw(context: CanvasRenderingContext2D, width: number, height: number, annotations: readonly ClnAnnotationView[], masks: readonly Mask[]) {
  context.lineWidth = Math.max(2, width / 200);
  for (const annotation of annotations) {
    context.strokeStyle = annotation.color ?? '#ffeb3b';
    const pts = annotation.points.map((point) => ({ x: Number(point.x) * width, y: Number(point.y) * height }));
    context.beginPath();
    if (annotation.kind === 'Marker' || annotation.kind === 'Note') {
      context.arc(pts[0]?.x ?? 0, pts[0]?.y ?? 0, width / 60, 0, Math.PI * 2);
    } else {
      pts.forEach((point, index) => (index === 0 ? context.moveTo(point.x, point.y) : context.lineTo(point.x, point.y)));
      if (annotation.kind === 'Area') {
        context.closePath();
      }
    }
    context.stroke();
  }
  context.fillStyle = '#000000';
  for (const mask of masks) {
    context.fillRect(mask.x * width, mask.y * height, mask.width * width, mask.height * height);
  }
}

export function ImageToolsDialog({ imageId, patientId, area, label, onClose }: { imageId: string; patientId: string; area: string; label: string; onClose: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  const canAnnotate = full && access.can('cln.image.capture');
  const canExport = full && access.can('cln.image.export');
  const { url, error } = useClinicalImage(imageId);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<Tool>('Marker');
  const [points, setPoints] = useState<{ x: number; y: number }[]>([]);
  const [text, setText] = useState('');
  const [valueMm, setValueMm] = useState('');
  const [masks, setMasks] = useState<Mask[]>([]);
  const [purpose, setPurpose] = useState<NonNullable<ClnImageExportPurpose>>('Marketing');
  const [withAnnotations, setWithAnnotations] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const annotations = useQuery({
    queryKey: keys.annotations(tenantId, imageId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/images/{id}/annotations', { params: { path: { id: imageId } } })),
  });
  const consents = useQuery({
    queryKey: keys.consents(tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/image-consents', { params: { path: { patientId } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.annotations(tenantId, imageId) });
  const add = useMutation({
    mutationFn: (kind: ClnAnnotationKind) =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/images/{id}/annotations', {
          params: { path: { id: imageId } },
          body: { kind, points, label: text.trim() || null, valueMm: kind === 'Measure' && valueMm ? Number(valueMm) : null, color: null },
        }),
      ),
    onSuccess: () => {
      setPoints([]);
      setText('');
      setValueMm('');
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (annotationId: string) => unwrap(tenantApi.DELETE('/api/v1/clinical/images/{id}/annotations/{annotationId}', { params: { path: { id: imageId, annotationId } } })),
    onSuccess: refresh,
  });
  const share = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/clinical/images/{id}:share', { params: { path: { id: imageId } } })),
    onSuccess: () => setNotice(t('cln:image_tools.shared')),
  });
  const exportImage = useMutation({
    mutationFn: async () => {
      const image = imageRef.current;
      const canvas = document.createElement('canvas');
      const width = image?.naturalWidth || 600;
      const height = image?.naturalHeight || 800;
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context || !image) {
        throw new Error('canvas_unavailable');
      }
      context.drawImage(image, 0, 0, width, height);
      draw(context, width, height, withAnnotations ? (annotations.data?.items ?? []) : [], masks);
      const png = await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('png_failed'))), 'image/png'));
      const form = new FormData();
      // نام فایل بدون نام مراجعه‌کننده (C-AGENT-1474).
      form.append('file', png, 'export.png');
      form.append('purpose', purpose);
      form.append('masks', JSON.stringify(masks));
      const response = await tenantApi.POST('/api/v1/clinical/images/{id}:export', {
        params: { path: { id: imageId } },
        body: form as never,
        bodySerializer: (value: unknown) => value as FormData,
        parseAs: 'blob',
      });
      if (!response.response.ok) {
        throw response.error ?? new Error(String(response.response.status));
      }
      return response.data as unknown as Blob;
    },
    onSuccess: (blob) => {
      setNotice(t('cln:image_tools.exported'));
      if (typeof URL.createObjectURL === 'function') {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'phoenix-export.png';
        link.click();
        URL.revokeObjectURL(link.href);
      }
    },
  });

  const onClick = (event: MouseEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const point = { x: Math.min(1, Math.max(0, (event.clientX - box.left) / (box.width || 1))), y: Math.min(1, Math.max(0, (event.clientY - box.top) / (box.height || 1))) };
    if (tool === 'Mask') {
      const next = [...points, point];
      if (next.length === 2 && next[0] && next[1]) {
        const [a, b] = next;
        setMasks((current) => [...current, { kind: 'Custom', x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width: Math.max(0.01, Math.abs(a.x - b.x)), height: Math.max(0.01, Math.abs(a.y - b.y)) }]);
        setPoints([]);
      } else {
        setPoints(next);
      }
      return;
    }
    setPoints((current) => (current.length >= (tool === 'Area' ? 64 : NEEDED[tool]) ? [point] : [...current, point]));
  };
  const kind = tool === 'Mask' ? null : tool;
  const ready = kind !== null && points.length >= NEEDED[kind] && (kind !== 'Note' || text.trim() !== '');
  const consent = consents.data;
  const allowed = (value: NonNullable<ClnImageExportPurpose>) => (value === 'Marketing' ? consent?.marketingConsent : consent?.educationConsent) === true;
  const faceArea = (FACE_AREAS as readonly string[]).includes(area);
  const needsFaceMask = faceArea && !masks.some((mask) => mask.kind === 'Eyes' || mask.kind === 'Face');

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth data-screen-id={SCR_STAFF_505} data-testid="cln-image-tools">
      <DialogTitle>{label}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
            {error ? (
              <ErrorState error={error} compact />
            ) : url ? (
              <Box component="img" ref={imageRef} src={url} alt={label} draggable={false} sx={{ width: '100%', display: 'block', userSelect: 'none' }} data-testid="cln-image-tools-image" />
            ) : (
              <Box sx={{ aspectRatio: '3 / 4', bgcolor: 'action.hover' }} />
            )}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              role="img"
              aria-label={t('cln:image_tools.layer', { count: annotations.data?.items.length ?? 0 })}
              onClick={canAnnotate || canExport ? onClick : undefined}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: canAnnotate ? 'crosshair' : 'default' }}
              data-testid="cln-image-layer"
            >
              <defs>
                <marker id="cln-arrow" markerWidth="4" markerHeight="4" refX="2" refY="2" orient="auto">
                  <path d="M0,0 L4,2 L0,4 z" fill="#ffeb3b" />
                </marker>
              </defs>
              {(annotations.data?.items ?? []).map((item) => shape(item, item.id))}
              {kind && points.length > 0 && shape({ kind, points, color: '#03a9f4' }, 'draft')}
              {masks.map((mask, index) => (
                <rect key={index} x={mask.x * 100} y={mask.y * 100} width={mask.width * 100} height={mask.height * 100} fill="#000" data-testid="cln-image-mask" />
              ))}
            </svg>
          </Box>
          <Stack spacing={1.5} sx={{ width: { md: 300 } }}>
            {(canAnnotate || canExport) && (
              <ToggleButtonGroup exclusive size="small" value={tool} onChange={(_, value: Tool | null) => value && (setTool(value), setPoints([]))} aria-label={t('cln:image_tools.tool')} sx={{ flexWrap: 'wrap' }}>
                {TOOLS.filter((value) => (value === 'Mask' ? canExport : canAnnotate)).map((value) => (
                  <ToggleButton key={value} value={value} data-testid={`cln-image-tool-${value}`}>
                    {t(`cln:image_tools.tools.${value}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            {canAnnotate && kind && (
              <Stack spacing={1} data-testid="cln-annotation-form">
                <Typography variant="caption" color="text.secondary">
                  {t('cln:image_tools.points', { count: points.length, needed: NEEDED[kind] })}
                </Typography>
                <TextField size="small" label={t('cln:image_tools.label')} value={text} onChange={(event) => setText(event.target.value)} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'cln-annotation-label' } }} />
                {kind === 'Measure' && <TextField size="small" type="number" label={t('cln:image_tools.value_mm')} value={valueMm} onChange={(event) => setValueMm(event.target.value)} slotProps={{ htmlInput: { min: 0, 'data-testid': 'cln-annotation-mm' } }} />}
                {add.error ? <ErrorState error={add.error} compact /> : null}
                <Button variant="contained" disabled={!ready || add.isPending} onClick={() => kind && add.mutate(kind)} data-testid="cln-annotation-save">
                  {t('cln:image_tools.save_annotation')}
                </Button>
              </Stack>
            )}
            {annotations.isPending ? (
              <LoadingState rows={2} />
            ) : (
              <List dense aria-label={t('cln:image_tools.annotations')} data-testid="cln-annotation-list">
                {(annotations.data?.items ?? []).map((item) => (
                  <ListItem
                    key={item.id}
                    secondaryAction={
                      canAnnotate ? (
                        <Button size="small" color="error" onClick={() => remove.mutate(item.id)} data-testid="cln-annotation-remove">
                          {t('cln:image_tools.remove')}
                        </Button>
                      ) : undefined
                    }
                  >
                    <ListItemText primary={`${t(`cln:image_tools.tools.${item.kind}`)}${item.valueMm !== null && item.valueMm !== undefined ? ` · ${String(item.valueMm)} mm` : ''}`} secondary={item.label ?? undefined} />
                  </ListItem>
                ))}
              </List>
            )}
            {canExport && (
              <Stack spacing={1} data-testid="cln-image-export">
                <Typography variant="subtitle2" component="h3">
                  {t('cln:image_tools.export_title')}
                </Typography>
                <TextField select size="small" label={t('cln:image_tools.purpose')} value={purpose} onChange={(event) => setPurpose(event.target.value as NonNullable<ClnImageExportPurpose>)} slotProps={{ htmlInput: { 'data-testid': 'cln-export-purpose' } }}>
                  {PURPOSES.map((value) => (
                    <MenuItem key={value} value={value} disabled={!allowed(value)}>
                      {t(`cln:image_tools.purposes.${value}`)}
                      {!allowed(value) ? ` · ${t('cln:image_tools.no_consent')}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
                {!allowed(purpose) && (
                  <Alert severity="warning" data-testid="cln-export-consent-missing">
                    {t('cln:image_tools.consent_required')}
                  </Alert>
                )}
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setMasks((current) => [...current, PRESETS.Eyes])} data-testid="cln-mask-eyes">
                    {t('cln:image_tools.mask_eyes')}
                  </Button>
                  <Button size="small" onClick={() => setMasks((current) => [...current, PRESETS.Face])} data-testid="cln-mask-face">
                    {t('cln:image_tools.mask_face')}
                  </Button>
                  {masks.length > 0 && (
                    <Button size="small" onClick={() => setMasks([])}>
                      {t('cln:image_tools.clear_masks')}
                    </Button>
                  )}
                </Stack>
                {needsFaceMask && <Typography variant="caption" color="warning.main" data-testid="cln-export-face-mask">{t('cln:image_tools.face_mask_required')}</Typography>}
                <FormControlLabel control={<Checkbox checked={withAnnotations} onChange={(event) => setWithAnnotations(event.target.checked)} />} label={t('cln:image_tools.with_annotations')} />
                {exportImage.error ? <ErrorState error={exportImage.error} compact /> : null}
                <Button variant="contained" disabled={!allowed(purpose) || needsFaceMask || exportImage.isPending || !url} onClick={() => exportImage.mutate()} data-testid="cln-export-submit">
                  {t('cln:image_tools.export')}
                </Button>
              </Stack>
            )}
            {full && access.can('cln.image.view') && access.can('doc.document.share') && (
              <Stack spacing={1}>
                {consent && !consent.dataProcessingConsent && <Alert severity="info">{t('cln:image_tools.share_consent')}</Alert>}
                {share.error ? <ErrorState error={share.error} compact /> : null}
                <Button onClick={() => share.mutate()} disabled={share.isPending || consent?.dataProcessingConsent === false} data-testid="cln-image-share">
                  {t('cln:image_tools.share_portal')}
                </Button>
              </Stack>
            )}
            {notice && <Alert severity="success" data-testid="cln-image-tools-notice">{notice}</Alert>}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cln:common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
